import { h, Fragment } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import useIsInViewport from 'utils/isInViewPort';
import setAttributes from 'utils/set-attributes';
import updateURL from 'utils/update-url';
import { trackPDPToggleClick, trackPDPAddToBag, trackPDPShippingClick, trackPDPBundleClick } from 'utils/tracking';
import QuizIcon from 'utils/icon/icon-quiz.svg';
import ChevronDown from '../utils/icon/chevron-down.svg';
import { withProductContext } from './product-context';
import VariantSelector from './variant-selector';
import { KeyDetails } from './details-with-flyout-panel';
import ProductQuantity from './product-quantity';
import ProductDetails from './product-details';
import { ShopPay } from './shop-pay';
import ProductAppBlocks from './product-app-blocks';
import MaxOrderMessage from './max-order-message';
import GiftCardForm from './gift-card-form';
import ProductOffers from './product-offers';
import ProductAccordion from './product-accordion';
import ProductUpsellForm from './product-upsell-form';
import { InventoryMessage, showInventoryMessageFn } from './inventory-message';

const CustomBundleItem = ({ data }) => {
  const { image, title, displayName, description, price, compareAtPrice, percentageOff, ctaLink } = data;
  const ctaHref = typeof ctaLink === 'string' ? ctaLink : (ctaLink && (ctaLink.url || ctaLink.href)) || null;
  const ctaLabel = typeof ctaLink === 'object' ? (ctaLink.title || ctaLink.anchor || ctaLink.text || ctaLink.label) : null;

  return (
    <div className="flex flex-row items-center space-x-4 lg:min-w-[340px] bg-[#F9F6F1] mb-[8px] p-[12px]">
      <div className="flex-shrink-0">
        {image ? (
          ctaHref ? (
            <a
              href={ctaHref}
              className="block object-contain w-[100px] h-[100px] lg:w-[120px] lg:h-[120px] overflow-hidden"
              dangerouslySetInnerHTML={{ __html: image }}
            />
          ) : (
            <div
              className="block object-contain w-[100px] h-[100px] lg:w-[120px] lg:h-[120px] overflow-hidden"
              dangerouslySetInnerHTML={{ __html: image }}
            />
          )
        ) : (
          ctaHref ? (
            <a href={ctaHref} className="w-[100px] h-[100px] lg:w-[120px] lg:h-[120px] bg-gray-200 flex items-center justify-center">
              <span className="text-xs text-gray-500">No Image</span>
            </a>
          ) : (
            <div className="w-[100px] h-[100px] lg:w-[120px] lg:h-[120px] bg-gray-200 flex items-center justify-center">
              <span className="text-xs text-gray-500">No Image</span>
            </div>
          )
        )}
      </div>

      <div className="product-upsell-content flex-grow flex flex-col space-y-2">
        <div className="product-upsell-content-col lg:min-w-[155px]">
          <h3 className="font-medium leading-[20px] mb-0">{displayName || title || 'Custom Bundle Item'}</h3>
          {description && (
            <div>
              <p className="leading-[20px] text-custom-black" dangerouslySetInnerHTML={{ __html: description }} />
            </div>
          )}
          {(price || compareAtPrice) && (
            <div className="mt-1">
              <div className="flex items-center space-x-2">
                {compareAtPrice && (
                  <s className="text-sm text-gray-500">${compareAtPrice}</s>
                )}
                {price && (
                  <span className={`text-sm ${compareAtPrice ? 'text-red' : ''}`}>${price}</span>
                )}
                {percentageOff && (
                  <span className={`text-sm ${compareAtPrice ? 'text-red' : ''}`}>({percentageOff}% off)</span>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="product-upsell-atc-col ml-0">
          {ctaLabel && ctaHref && (
            <div className="flex-grow atc-wrapper">
              <a
                href={ctaHref}
                className="atc-button button--upsell h-[40px] text-sm inline-flex items-center justify-center"
              >
                {ctaLabel}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Breadcrumbs = ({ breadcrumbs, primaryCollection, secondaryCollection, thirdCollection }) => {
  const primaryCollectionPath = `/collections/${primaryCollection.name}/`;
  const primaryCollectionName = primaryCollection?.name
    ? primaryCollection.name
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  const primaryCollectionLink = {
    url: primaryCollectionPath,
    title: primaryCollectionName,
  };
  const secondaryCollectionPath = `/collections/${secondaryCollection.name}/`;
  const secondaryCollectionName = secondaryCollection?.name
    ? secondaryCollection.name
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  const secondaryCollectionLink = {
    url: secondaryCollectionPath,
    title: secondaryCollectionName,
  };
  const thirdCollectionPath = `/collections/${thirdCollection.name}/`;
  const thirdCollectionName = thirdCollection?.name
    ? thirdCollection.name
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  const thirdCollectionLink = {
    url: thirdCollectionPath,
    title: thirdCollectionName,
  };

  const breadcrumbsArray = [
    breadcrumbs.home,
    primaryCollectionLink,
    secondaryCollectionLink,
    thirdCollectionLink,
  ].filter((crumb) => crumb && crumb.url);

  const shouldRender =
    !primaryCollectionLink.url.includes('undefined') ||
    !secondaryCollectionLink.url.includes('undefined') ||
    !thirdCollectionLink.url.includes('undefined');

  return shouldRender ? (
    <nav className="breadcrumb mb-2" role="navigation" aria-label="breadcrumbs">
      {breadcrumbsArray.map((breadcrumb, index) => (
        <Fragment key={breadcrumb.title}>
          {index > 0 && (
            <span className="mr-2 ml-2 inline-block" aria-hidden="true">
              /
            </span>
          )}
          <a href={breadcrumb.url} title={breadcrumb.title} className="underline text-link-light-2">
            {breadcrumb.title}
          </a>
        </Fragment>
      ))}
    </nav>
  ) : null;
};

const Price = ({ currentVariant, product }) => {
  const isNotForSale = product?.tags?.includes('not-for-sale');
  
  if (isNotForSale) {
    return null;
  }
  
  return (
    <div className="mb-4">
      {currentVariant.compareAtPrice && parseFloat(currentVariant.compareAtPrice.amount) > 0 && (
        <s className="price-tag mr-1">{formatMoney(currentVariant.compareAtPrice)}</s>
      )}
      <span className={`price-tag ${currentVariant.compareAtPrice?.amount ? 'text-red' : ''}`}>
        {formatMoney(currentVariant.price)}
      </span>
    </div>
  );
};

const SingleBundleChildren = ({ items, detailsFlyout }) => {
  const [openIndexes, setOpenIndexes] = useState([]);

  const toggleAccordion = (index) => {
    setOpenIndexes((prevIndexes) =>
      prevIndexes.includes(index) ? prevIndexes.filter((i) => i !== index) : [...prevIndexes, index],
    );
  };

  const getTotalItemsCount = () => {
    if (!items?.length) return 0;
    
    const totalFromNumberOfItems = items.reduce((total, item) => {
      const numberOfItems = parseInt(item.number_of_items, 10);
      return total + (numberOfItems || 0);
    }, 0);

    return totalFromNumberOfItems > 0 ? totalFromNumberOfItems : items.length;
  };

  const createDetailsModal = () => {
    const modal = document.createElement('div');
    modal.className =
      'bundle-details-modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2147483637]';
    modal.innerHTML = `
      <div class="bundle-details-modal-content bg-white p-6 max-w-2xl w-full mx-4">
        <div class="flex justify-end items-center mb-4">
          <button class="text-gray-400 hover:text-gray-600">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            xmlns="http://www.w3.org/2000/svg">
              <path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19
              17.6L17.6 19L12 13.4L6.4 19Z" fill="#1D1B20"/>
            </svg>
          </button>
        </div>
        <div class="bundle-details-modal-body max-h-[80vh] overflow-y-auto">
          ${detailsFlyout.flyoutContentMiddle ? `<div class="rte">${detailsFlyout.flyoutContentMiddle}</div>` : ''}
          ${detailsFlyout.flyoutContentEnd ? `<div class="rte">${detailsFlyout.flyoutContentEnd}</div>` : ''}
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    const removeModal = () => {
      modal.remove();
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = '';
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        removeModal();
      }
    };

    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        removeModal();
      }
    });

    modal.querySelector('button').onclick = removeModal;
    document.addEventListener('keydown', handleEscapeKey);
  };


  return !!items?.length && (
    <div className="pb-2.5 border-t border-alt-black bundle-items-container">
      <div className="flex items-center justify-between py-4">
        <span className="flex items-center font-medium">
          <span className="bundle-title-name">{window.TMW.Strings.bundle.bundleTitle}</span>
          <span className="number-of-items-count">&nbsp;({getTotalItemsCount()})</span>
        </span>
      </div>
      <div className="bundle-items-container-inner px-[12px] bg-[#F9F6F1]">
        {items?.map((item, index) => {
          if (item.accordion_content) {
            // Render as accordion if accordion_content is present
            return (
              <div className={`bundle-item-row ${index === items.length - 1 ? '' : 'border-b border-[#D6CCC7]'}`} key={item.title}>
                <button
                  className="flex justify-between items-center w-full py-4 text-left mb-0"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleAccordion(index);
                  }}
                >
                    <div className="single-bundle-item-row-info w-full flow md:block">
                      <div className="block md:flex items-baseline justify-between">
                        <p className="text-[#402518] leading-[110%] md:max-w-[calc(100%-132px)]">{item.title} {item.options ? `- ${item.options}` : ''}</p>
                        {item.badge_text && <span className="menu-link-tag !mr-8 !h-auto mb-0 !hidden md:!inline-block">{item.badge_text}</span>}
                      </div>
                      {item.shortDescription && (
                        <p className="text-[#402518] font-normal text-[13px] leading-[18px] mt-1">{item.shortDescription}</p>
                      )}
                      {item.options && (
                        <p className="text-link-light text-[#402518] text-[13px] leading-[18px] mt-1">{item.options}</p>
                      )}
                      {item.badge_text && <span className="menu-link-tag !h-auto mt-1 inline-flex w-auto md:!hidden">{item.badge_text}</span>}
                    </div>
                  <ChevronDown
                    className={`transition-transform duration-200 ${
                      openIndexes.includes(index) ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndexes.includes(index) && (
                  <div className="pb-4 px-5 single-bundle-item-row-accordion-container">
                    <div 
                      className="rte text-body-2 text-[#402518]"
                      dangerouslySetInnerHTML={{ __html: item.accordion_content }}
                    />
                  </div>
                )}
              </div>
            );
          }
          
          // Render as regular item if no accordion_content
          return (
              <div className="flow md:flex flex-1 flex-col justify-center bg-[#F9F6F1] py-5 bundle-item-row border-b border-[#D6CCC7]" key={item.title}>
                <div className="block md:flex items-center justify-between">
                  <p className="text-[#402518] leading-[110%] md:max-w-[calc(100%-132px)]">{item.title}</p>
                  {item.badge_text && <span className="menu-link-tag !mr-8 !h-auto mb-0 !hidden md:!inline-block">{item.badge_text}</span>}
                </div>
                {item.shortDescription && (
                  <p className="text-[#402518] font-normal text-[13px] leading-[18px] mt-1">{item.shortDescription}</p>
                )}
                {item.options && (
                  <p className="text-link-light text-[#402518] text-[13px] leading-[18px] mt-1">{item.options}</p>
                )}
                {item.badge_text && <span className="menu-link-tag !h-auto mt-1 inline-flex w-auto md:!hidden">{item.badge_text}</span>}
              </div>
          );
        })}
      </div>
      {detailsFlyout.flyoutContentMiddle && (
        <button
          className="accordion-modal-button underline mt-[16px] text-left !tracking-[0px] font-normal text-[#402518] text-[13px] leading-[18px]"
          type="button"
          onClick={createDetailsModal}
        >
          View Full Details
        </button>
      )}
    </div>
  );
};

const AddToCartButton = ({ currentVariant, qty, product, giftForm, isGiftCard }) => {
  const isNotForSale = product?.tags?.includes('not-for-sale');
  
  if (isNotForSale) {
    return null;
  }

  const handleAddToCart = (e) => {
    e.preventDefault();
    let attributes = setAttributes(product);

    if (isGiftCard && giftForm.current) {
      const giftFormState = giftForm.current.getFormState();
      if (!giftFormState) {
        return;
      }
      const giftCardAttributes = Object.entries(giftFormState)
        .map(([k, v]) => (v === '' ? undefined : { key: k, value: v }))
        .filter((v) => v);

      attributes = [...attributes, ...giftCardAttributes];
    }

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          ...window.getPayload(currentVariant, qty),
          attributes,
        },
      },
    });
    document.dispatchEvent(addToCartEvent);

    trackPDPAddToBag({
      item: product.title,
      itemId: currentVariant.sku,
      quantity: qty,
    });
  };

  return (
    <button
      type="submit"
      className="atc-button button button--primary w-full h-[50px]"
      disabled={!currentVariant.availableForSale}
      onClick={(e) => handleAddToCart(e)}
    >
      {currentVariant
        ? currentVariant?.availableForSale
          ? window.TMW.Strings.product.addToCart
          : window.TMW.Strings.product.outOfStock
        : window.TMW.Strings.product.unavailable}
    </button>
  );
};

const AddToCartSticky = ({ children }) => (
  <div className="fixed bottom-0 left-0 right-0 z-50 bg-white atc-wrapper">{children}</div>
);

const Shipping = ({ freeShipping: { freeShippingText, standardShipping } }) => {
  const freeShippingTitle = standardShipping
    ? window.TMW.Strings.product.standard_shipping
    : window.TMW.Strings.product.shipping_and_returns;

  const handleShippingFlyoutPanel = (e) => {
    trackPDPShippingClick(freeShippingTitle);
    const openShippingFlyoutPanel = new CustomEvent('shippingFlyoutPanel:open', {
      detail: {
        el: e.target,
      },
    });
    document.dispatchEvent(openShippingFlyoutPanel);
  };

  return (
    <div className="flex items-center gap-2 mt-[20px] mb-[20px]">
      {freeShippingText && <p>{freeShippingText}</p>}
      &bull;
      <button
        className="underline"
        type="button"
        onClick={handleShippingFlyoutPanel}
        tabIndex={0}
        aria-label={freeShippingTitle}
        title={freeShippingTitle}
      >
        {freeShippingTitle}
      </button>
    </div>
  );
};

const PromoMessage = ({ product, promoRef }) => {
  if (
    !product.promo.promo_message_content_01 &&
    !product.promo.promo_message_content_02 &&
    !product.promo.promo_message_title_01 &&
    !product.promo.promo_message_title_02
  )
    return false;

  return (
    <div className="my-5" ref={promoRef}>
      {[1, 2].map((index) => (
        <>
          {product.promo[`promo_message_title_0${index}`] && (
            <div className="bg-pantone-8 px-5 py-4 mb-px">
              <h4 className="text-headline-tag">{product.promo[`promo_message_title_0${index}`]}</h4>
              <div
                className="rte text-body-2 [&>div>p]:!py-0 [&>div>p]:!border-t-0"
                dangerouslySetInnerHTML={{
                  __html: product.promo[`promo_message_content_0${index}`],
                }}
              />
            </div>
          )}
        </>
      ))}
    </div>
  );
};

// this regular product form is also being used for a type of product that the client refers to as a "virtual bundle"
// it has already been pre-bundled, instead of letting the customer build their own bundle
// per SAJ-1563 we need to add a condition here to check for that type of bundle
// if it is a pre-bundle, then don't render the additional flyout trigger found in <KeyDetails />
const KeyDetailsWithFlyout = ({ isPrebundle, detailsFlyout }) => {
  const { flyoutTitle } = detailsFlyout;

  if (!flyoutTitle) return null;

  return flyoutTitle ? (
    <Fragment>
      {!isPrebundle && <KeyDetails detailsFlyout={detailsFlyout} />}
    </Fragment>
  ) : null;
};

const ProductForm = ({
  currentVariant,
  setCurrentVariant,
  product,
  layout,
  freeShipping,
  detailsFlyout,
  singleBundleItems,
  actualProduct,
  maxOrderQTY,
  templateSuffix,
  showGiftCardForm,
  quizCopy,
  quizCtaText,
  quizCtaUrl,
  breadcrumbs,
  primaryCollection,
  secondaryCollection,
  thirdCollection,
  pdpOffers,
  accordions,
  pairsWellWithTab1: pairsWellWithProduct1,
  pairsWellWithTab2: pairsWellWithProduct2,
  saveWithSetsTab1: saveWithSetsProduct1,
  saveWithSetsTab2: saveWithSetsProduct2,
  saveWithSetsTitle,
  pairsWellWithTitle,
  inventoryMessageData,
  customBundleTab
}) => {
  const atcRef = useRef(null);
  const isMobile = layout.bps.below.sm.matches;
  const isInViewPort = useIsInViewport(atcRef);
  const [qty, setQty] = useState(1);
  const isGiftCard = templateSuffix === 'gift_card';
  const giftForm = useRef(null);
  const promoRef = useRef(null);
  const pdpAccordions = {
    accordions,
    productIcons: detailsFlyout.productIcons,
  };

  const [activeTab, setActiveTab] = useState('saveWithSets');

  const hasPairsWellWith =
    (pairsWellWithProduct1.product?.available && pairsWellWithProduct1.description) ||
    (pairsWellWithProduct2.product?.available && pairsWellWithProduct2.description);

  const hasSaveWithSets =
    (saveWithSetsProduct1.product?.available && saveWithSetsProduct1.description) ||
    (saveWithSetsProduct2.product?.available && saveWithSetsProduct2.description) ||
    (customBundleTab && Array.isArray(customBundleTab) && customBundleTab.some(item => item.title));

  const renderUpsellTabs = () => {
    // if this condition is not met, there is another return below that will render a slightly different version of the upsell tabs
    if ((hasPairsWellWith && !hasSaveWithSets) || (!hasPairsWellWith && hasSaveWithSets)) {
      return (
        <div className="upsell-container">
          <div className="upsell-content">
            {hasSaveWithSets ? (
              <>
                <h3 className="upsell-title mb-[6px] font-medium">{saveWithSetsTitle || 'Save with sets'}</h3>
                <div className="upsell-tab-2">
                  {customBundleTab && Array.isArray(customBundleTab) && customBundleTab.map((item, index) =>
                    item.title && <CustomBundleItem key={`custom-bundle-${index}`} data={item} />
                  )}
                  {saveWithSetsProduct1.product?.available && saveWithSetsProduct1.description && (
                    <ProductUpsellForm data={saveWithSetsProduct1} />
                  )}
                  {saveWithSetsProduct2.product?.available && saveWithSetsProduct2.description && (
                    <ProductUpsellForm data={saveWithSetsProduct2} />
                  )}
                </div>
              </>
            ) : (
              <>
                <h3 className="upsell-title mb-[6px] font-medium">{pairsWellWithTitle || 'Pairs well with'}</h3>
                <div className="upsell-tab-2">
                  {pairsWellWithProduct1.product?.available && pairsWellWithProduct1.description && (
                    <ProductUpsellForm data={pairsWellWithProduct1} />
                  )}
                  {pairsWellWithProduct2.product?.available && pairsWellWithProduct2.description && (
                    <ProductUpsellForm data={pairsWellWithProduct2} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="upsell-container">
        {(hasPairsWellWith || hasSaveWithSets) && (
          <div className="upsell-tabs mb-[8px] border-b border-[#827F7D]">
            {hasSaveWithSets && (
              <button
                type="button"
                className={`tab-button ${activeTab === 'saveWithSets' ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('saveWithSets');
                }}
              >
                {saveWithSetsTitle || 'Save with sets'}
              </button>
            )}
            {hasPairsWellWith && (
              <button
                type="button"
                className={`tab-button ${activeTab === 'pairsWellWith' ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('pairsWellWith');
                }}
              >
                {pairsWellWithTitle || 'Pairs well with'}
              </button>
            )}
          </div>
        )}
        <div className="upsell-content">
          {activeTab === 'saveWithSets' && hasSaveWithSets && (
            <div className="upsell-tab-2">
              {customBundleTab && Array.isArray(customBundleTab) && customBundleTab.map((item, index) =>
                item.title && <CustomBundleItem key={`custom-bundle-tab-${index}`} data={item} />
              )}
              {saveWithSetsProduct1.product?.available && saveWithSetsProduct1.description && (
                <ProductUpsellForm data={saveWithSetsProduct1} />
              )}
              {saveWithSetsProduct2.product?.available && saveWithSetsProduct2.description && (
                <ProductUpsellForm data={saveWithSetsProduct2} />
              )}
            </div>
          )}
          {activeTab === 'pairsWellWith' && hasPairsWellWith && (
            <div className="upsell-tab-1">
              {pairsWellWithProduct1.product?.available && pairsWellWithProduct1.description && (
                <ProductUpsellForm data={pairsWellWithProduct1} />
              )}
              {pairsWellWithProduct2.product?.available && pairsWellWithProduct2.description && (
                <ProductUpsellForm data={pairsWellWithProduct2} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    // track promo message links click
    if (promoRef && promoRef.current) {
      const links = promoRef.current.querySelectorAll('a');
      if (!links.length) return;

      links.forEach((link) =>
        link.addEventListener('click', (e) => {
          e.preventDefault();
          trackPDPBundleClick(link.innerText);
          // if the link has target="_blank", open in new tab
          if (link.getAttribute('target') === '_blank') {
            window.open(updateURL(link.href), '_blank');
          } else {
            window.location = updateURL(link.href);
          }
        }),
      );
    }
  }, [promoRef]);

  useEffect(() => {
    if (window.Klarna?.OnsiteMessaging) {
      window.Klarna.OnsiteMessaging.refresh();
    }
  }, [currentVariant?.price]);

  const hasOnlyDefaultVariant = product?.variants?.hasOnlyDefaultVariant || false;

  const variantSelectors = useMemo(
    () =>
      !hasOnlyDefaultVariant &&
      product?.options?.map((option) => (
        <VariantSelector
          currentVariant={currentVariant}
          product={product}
          key={option.name}
          option={option}
          setCurrentVariant={setCurrentVariant}
          isPDP={true}
        />
      )),
    [currentVariant, product],
  );

  const price = useMemo(() => {
    const productId = product?.id ? product.id.split('Product/')[1] : null;
    return <Price currentVariant={currentVariant} product={actualProduct} id={productId} />;
  }, [currentVariant, product]);

  const addToCartButton = useMemo(
    () => (
      <AddToCartButton
        currentVariant={currentVariant}
        qty={qty}
        product={actualProduct}
        maxOrderQTY={maxOrderQTY}
        giftForm={giftForm}
        isGiftCard={isGiftCard}
      />
    ),
    [currentVariant, actualProduct, qty],
  );

  const productQuantity = useMemo(
    () => (
      <ProductQuantity
        quantity={qty}
        changeQuantity={(value) => {
          setQty(value);
          trackPDPToggleClick(`quantity ${value}`);
        }}
        maxOrder={maxOrderQTY?.max_order_qty}
      />
    ),
    [(currentVariant, product, qty)],
  );

  const addToCartSticky = useMemo(() => {
    const isNotForSale = actualProduct?.tags?.includes('not-for-sale');
    
    if (isNotForSale) {
      return null;
    }
    
    return (
      <AddToCartSticky>
        <AddToCartButton
          currentVariant={currentVariant}
          product={actualProduct}
          qty={1}
          giftForm={giftForm}
          isGiftCard={isGiftCard}
        />
      </AddToCartSticky>
    );
  }, [currentVariant, actualProduct]);

  const showInventoryMessage = showInventoryMessageFn(currentVariant);

  const shipping = useMemo(() => <Shipping freeShipping={freeShipping} />, [freeShipping]);

  // eslint-disable-next-line max-len
  const keyDetailsWithFlyout = useMemo(() => <KeyDetailsWithFlyout isPrebundle={singleBundleItems?.length > 0} detailsFlyout={detailsFlyout} />, [detailsFlyout]);

  const promo = useMemo(() => <PromoMessage product={actualProduct} promoRef={promoRef} />, [actualProduct]);

  const singleBundleChildren = useMemo(
    () => <SingleBundleChildren items={singleBundleItems} detailsFlyout={detailsFlyout} />,
    [product, detailsFlyout],
  );

  const maxOrderMessage = useMemo(
    () => (
      <MaxOrderMessage maxOrder={maxOrderQTY?.max_order_qty} errorMessage={maxOrderQTY?.error_message} quantity={qty} />
    ),
    [maxOrderQTY, qty],
  );

  const [truncateText, setTruncateText] = useState(false);
  const [descriptionState, setDescriptionState] = useState({
    showFullDescription: false,
    eventFired: false,
  });
  const { showFullDescription } = descriptionState;

  const toggleDescription = (e) => {
    e.preventDefault();

    const updatedState = {
      ...descriptionState,
      showFullDescription: !descriptionState.showFullDescription,
      eventFired: true,
    };

    if (!descriptionState.eventFired) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer?.push({
        event: 'pdp_read_more_click',
        page: 'PDP',
        action: 'Read More Click',
        text: `${actualProduct.title} - ${currentVariant.sku}`,
      });
    }

    setDescriptionState(updatedState);
  };

  const productDescription = useMemo(() => {
    const words = actualProduct.description
      .replace(/<meta charset="utf-8">/g, '')
      .replace(/<br data-mce-fragment="1">/g, '');
    const productDescriptionRef = useRef(null);

    const calculateLineCount = () => {
      if (productDescriptionRef.current) {
        const style = window.getComputedStyle(productDescriptionRef.current);
        const lineHeight = parseFloat(style.lineHeight);
        const paddingTop = parseFloat(style.paddingTop);
        const paddingBottom = parseFloat(style.paddingBottom);
        const borderTop = parseFloat(style.borderTopWidth);
        const borderBottom = parseFloat(style.borderBottomWidth);
        const height = Math.round(productDescriptionRef.current.offsetHeight);
        const count = Math.floor((height - paddingTop - paddingBottom - borderTop - borderBottom) / lineHeight);
        if (count >= 4) {
          productDescriptionRef.current.classList.add('truncate-3-lines');
          setTruncateText(true);
        } else if (count <= 3) {
          setTruncateText(false);
        }
      }
    };

    useEffect(() => {
      calculateLineCount();

      const handleResize = () => {
        productDescriptionRef.current.classList.remove('truncate-3-lines');
        calculateLineCount();
      };

      const throttleAndDebounce = (func, throttleTime, debounceTime) => {
        let timeoutId = null;
        let lastRunTime = 0;

        return function throttledAndDebounced(...args) {
          const context = this;
          const now = Date.now();
          const timeSinceLastRun = now - lastRunTime;
          const shouldThrottle = timeSinceLastRun < throttleTime;

          clearTimeout(timeoutId);

          if (shouldThrottle) {
            timeoutId = setTimeout(() => {
              lastRunTime = Date.now();
              func.apply(context, args);
            }, debounceTime - timeSinceLastRun);
          } else {
            lastRunTime = now;
            func.apply(context, args);
          }
        };
      };

      const throttledAndDebouncedHandleResize = throttleAndDebounce(handleResize, 50, 50);
      window.addEventListener('resize', throttledAndDebouncedHandleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    return (
      <div className="mb-[15px]">
        <div className={`rte text-body-2`}>
          <div
            className={`${truncateText && !descriptionState.showFullDescription ? 'truncate-3-lines' : ''}`}
            ref={productDescriptionRef}
            dangerouslySetInnerHTML={{ __html: words }}
          />
        </div>
        {!truncateText && <div className="min-h-[26px] [&:empty]:hidden"></div>}
        {truncateText && !descriptionState.showFullDescription && (
          <button className="text-link cursor-pointer underline" onClick={toggleDescription}>
            Read More
          </button>
        )}
        {truncateText && descriptionState.showFullDescription && (
          <button className="text-link cursor-pointer underline" onClick={toggleDescription}>
            Read Less
          </button>
        )}
      </div>
    );
  }, [actualProduct, showFullDescription, truncateText]);

  return (
    <form
      method="post"
      action="/cart/add"
      acceptCharset="UTF-8"
      encType="multipart/form-data"
      noValidate="novalidate"
      data-product-form
      className='product-form'
    >
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        primaryCollection={primaryCollection}
        secondaryCollection={secondaryCollection}
        thirdCollection={thirdCollection}
      />

      <h1 className="headline-2 lg:mb-0.5">{actualProduct.title}</h1>
      {actualProduct?.subtitle && (
        <h2 className={`title-3 ${showInventoryMessage ? 'mb-2' : 'mb-[24px]'}`}>
          {(() => {
            const productSubtitleLowercase = actualProduct.subtitle.toLowerCase();
            return productSubtitleLowercase.charAt(0).toUpperCase() + productSubtitleLowercase.slice(1);
          })()}
        </h2>
      )}
      {showInventoryMessage && <InventoryMessage inventoryMessageData={inventoryMessageData} />}
      {price}
      <ProductAppBlocks classes='mt-2 mb-2.5' />
      {productDescription}
      {variantSelectors}
      {isGiftCard && showGiftCardForm && <GiftCardForm ref={giftForm} />}
      <div className="flex mt-[20px] mb-1.5">
        {!actualProduct?.tags?.includes('not-for-sale') && productQuantity}
        <div ref={atcRef} className="w-full atc-wrapper">
          {addToCartButton}
        </div>
      </div>
      {maxOrderQTY && maxOrderMessage}
      {!isGiftCard && currentVariant && !actualProduct?.tags?.includes('not-for-sale') && (
        <ShopPay price={currentVariant.price} available={currentVariant.availableForSale} />
      )}
      {!isGiftCard && shipping}
      {promo}
      {singleBundleChildren}
      {isMobile && !isInViewPort && addToCartSticky}
      <ProductDetails
        product={actualProduct}
        isBenefits={false}
        specialClass={detailsFlyout.flyoutTitle ? 'last-of-type:border-b-0 last-of-type:mb-0' : 'last-of-type:mb-5'}
      />
      {keyDetailsWithFlyout}
      <ProductAccordion data={pdpAccordions} />
      {quizCopy && quizCtaText && quizCtaUrl && (
        <div className="flex gap-4 px-5 py-4 mb-px bg-pantone-8 xl:items-center">
          <QuizIcon className="min-w-[40px] min-h-[40px] col-span-1 mt-3 xl:mt-0 lg:mr-2" />
          <div className="col-span-2 flex flex-col xl:flex-row xl:items-center">
            <div className="text-body-2 col-span-1 xl:col-span-2 mb-4 xl:mb-0 xl:mr-2">
              <p>{quizCopy}</p>
            </div>
            <div className="w-auto">
              <a href={quizCtaUrl} className="h-[50px] min-w-[130px] button button--primary">
                {quizCtaText}
              </a>
            </div>
          </div>
        </div>
      )}
      {renderUpsellTabs()}
      {pdpOffers && pdpOffers.length > 0 && <ProductOffers data={pdpOffers} />}
    </form>
  );
};

export default withProductContext(ProductForm);
